/**
 * Input validation utilities
 * Centralized validation to prevent data abuse and security issues
 */

export const VALIDATION_RULES = {
  POST_CONTENT_MAX: 5000,
  POST_CONTENT_MIN: 1,
  COMMENT_CONTENT_MAX: 2000,
  COMMENT_CONTENT_MIN: 1,
  BIO_MAX: 500,
  DISPLAY_NAME_MAX: 50,
  DISPLAY_NAME_MIN: 1,
  USERNAME_MIN: 3,
  USERNAME_MAX: 20,
  POLL_TITLE_MAX: 200,
  POLL_DESCRIPTION_MAX: 1000,
  POLL_OPTION_TEXT_MAX: 100,
} as const;

export const USERNAME_REGEX = /^[a-zA-Z0-9_]+$/;
export const URL_REGEX = /^https?:\/\/.+/;

export class ValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ValidationError';
  }
}

/**
 * Validate post content
 */
export function validatePostContent(content: string): void {
  if (!content || content.trim().length < VALIDATION_RULES.POST_CONTENT_MIN) {
    throw new ValidationError('El contenido del post no puede estar vacío');
  }
  if (content.length > VALIDATION_RULES.POST_CONTENT_MAX) {
    throw new ValidationError(`El contenido del post no puede exceder ${VALIDATION_RULES.POST_CONTENT_MAX} caracteres`);
  }
}

/**
 * Validate comment content
 */
export function validateCommentContent(content: string): void {
  if (!content || content.trim().length < VALIDATION_RULES.COMMENT_CONTENT_MIN) {
    throw new ValidationError('El comentario no puede estar vacío');
  }
  if (content.length > VALIDATION_RULES.COMMENT_CONTENT_MAX) {
    throw new ValidationError(`El comentario no puede exceder ${VALIDATION_RULES.COMMENT_CONTENT_MAX} caracteres`);
  }
}

/**
 * Validate bio
 */
export function validateBio(bio: string | null | undefined): void {
  if (bio && bio.length > VALIDATION_RULES.BIO_MAX) {
    throw new ValidationError(`La biografía no puede exceder ${VALIDATION_RULES.BIO_MAX} caracteres`);
  }
}

/**
 * Validate display name
 */
export function validateDisplayName(displayName: string): void {
  if (!displayName || displayName.trim().length < VALIDATION_RULES.DISPLAY_NAME_MIN) {
    throw new ValidationError('El nombre no puede estar vacío');
  }
  if (displayName.length > VALIDATION_RULES.DISPLAY_NAME_MAX) {
    throw new ValidationError(`El nombre no puede exceder ${VALIDATION_RULES.DISPLAY_NAME_MAX} caracteres`);
  }
}

/**
 * Validate username
 */
export function validateUsername(username: string): void {
  if (!username || username.length < VALIDATION_RULES.USERNAME_MIN) {
    throw new ValidationError(`El nombre de usuario debe tener al menos ${VALIDATION_RULES.USERNAME_MIN} caracteres`);
  }
  if (username.length > VALIDATION_RULES.USERNAME_MAX) {
    throw new ValidationError(`El nombre de usuario no puede exceder ${VALIDATION_RULES.USERNAME_MAX} caracteres`);
  }
  if (!USERNAME_REGEX.test(username)) {
    throw new ValidationError('El nombre de usuario solo puede contener letras, números y guiones bajos');
  }
}

/**
 * Validate avatar URL
 */
export function validateAvatarUrl(url: string | null | undefined): void {
  if (!url) return;

  // Only allow HTTPS URLs to prevent SSRF attacks
  if (!url.startsWith('https://')) {
    throw new ValidationError('La URL del avatar debe usar HTTPS');
  }

  // Whitelist allowed domains
  const allowedDomains = [
    'lh3.googleusercontent.com', // Google avatars
    'res.cloudinary.com', // Cloudinary
    'avatars.githubusercontent.com', // GitHub
  ];

  try {
    const urlObj = new URL(url);
    const isAllowed = allowedDomains.some(domain => urlObj.hostname.endsWith(domain));

    if (!isAllowed) {
      throw new ValidationError('La URL del avatar no es de un dominio permitido');
    }
  } catch (e) {
    if (e instanceof ValidationError) throw e;
    throw new ValidationError('URL de avatar inválida');
  }
}

/**
 * Validate poll title
 */
export function validatePollTitle(title: string): void {
  if (!title || title.trim().length === 0) {
    throw new ValidationError('El título de la encuesta no puede estar vacío');
  }
  if (title.length > VALIDATION_RULES.POLL_TITLE_MAX) {
    throw new ValidationError(`El título de la encuesta no puede exceder ${VALIDATION_RULES.POLL_TITLE_MAX} caracteres`);
  }
}

/**
 * Validate poll description
 */
export function validatePollDescription(description: string | null | undefined): void {
  if (description && description.length > VALIDATION_RULES.POLL_DESCRIPTION_MAX) {
    throw new ValidationError(`La descripción de la encuesta no puede exceder ${VALIDATION_RULES.POLL_DESCRIPTION_MAX} caracteres`);
  }
}

/**
 * Validate poll option text
 */
export function validatePollOptionText(text: string): void {
  if (!text || text.trim().length === 0) {
    throw new ValidationError('El texto de la opción no puede estar vacío');
  }
  if (text.length > VALIDATION_RULES.POLL_OPTION_TEXT_MAX) {
    throw new ValidationError(`El texto de la opción no puede exceder ${VALIDATION_RULES.POLL_OPTION_TEXT_MAX} caracteres`);
  }
}

/**
 * Sanitize HTML to prevent XSS
 * Note: React already escapes by default, but this is extra protection
 */
export function sanitizeHtml(html: string): string {
  // Basic sanitization - remove script tags and event handlers
  return html
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/on\w+="[^"]*"/gi, '')
    .replace(/on\w+='[^']*'/gi, '')
    .replace(/javascript:/gi, '');
}
